// src/stores/modules/auth.ts
import { auth, db } from "@/firebaseConfig";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateProfile,
  signOut,
  User,
} from "firebase/auth";
import {
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import i18n from "@/i18n";
import { getStoredLocale, syncLocaleFromUser } from "@/composables/useLanguage";

export interface AuthState {
  user: User | null;
  loggedIn: boolean;
  initialized: boolean;
  _unsub: null | (() => void);
  _readyPromise: null | Promise<User | null>;
}

const MAX_SESSION_MS = 12 * 60 * 60 * 1000; // 12 hours
const SESSION_5_MIN_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_15_MIN_MS = 15 * 60 * 1000; // 15 minutes
const SESSION_30_MIN_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_1_HOUR_MS = 60 * 60 * 1000; // 1 hour
const SESSION_6_HOUR_MS = 6 * 60 * 60 * 1000; // 6 hours
const SESSION_24_HOUR_MS = 24 * 60 * 60 * 1000; // 24 hours
const SESSION_TIMEOUT_MS = import.meta.env.DEV
  ? SESSION_24_HOUR_MS
  : MAX_SESSION_MS;

export const isSessionExpired = (user: User | null): boolean => {
  if (!user) return true;

  const raw = user.metadata?.lastSignInTime;
  const lastLoginMs = raw != null ? new Date(raw).getTime() : NaN;

  if (!Number.isFinite(lastLoginMs)) {
    console.warn(
      "Invalid lastLoginAt in Firebase user metadata:",
      user.metadata,
    );
    return false; // Avoid forced logout loop on malformed metadata
  }

  const elapsed = Date.now() - lastLoginMs;
  return elapsed > SESSION_TIMEOUT_MS;
};

const state: AuthState = {
  user: null,
  loggedIn: false,
  initialized: false,
  _unsub: null,
  _readyPromise: null,
};

const getters = {
  user: (s: AuthState) => s.user,
  isLoggedIn: (s: AuthState) => s.loggedIn,
  uid: (s: AuthState) => s.user?.uid ?? null,
  displayName: (s: AuthState) => s.user?.displayName ?? null,
  email: (s: AuthState) => s.user?.email ?? null,
  photoURL: (s: AuthState) => s.user?.photoURL ?? null,
};

const mutations = {
  SET_USER(s: AuthState, user: User | null) {
    s.user = user;
  },
  SET_LOGGED_IN(s: AuthState, v: boolean) {
    s.loggedIn = v;
  },
  SET_INITIALIZED(s: AuthState, v: boolean) {
    s.initialized = v;
  },
  SET_UNSUB(s: AuthState, fn: (() => void) | null) {
    if (s._unsub)
      try {
        s._unsub();
      } catch {}
    s._unsub = fn;
  },
  SET_READY_PROMISE(s: AuthState, p: Promise<User | null> | null) {
    s._readyPromise = p;
  },
  // Convenience local updates so the UI reflects changes instantly
  SET_DISPLAY_NAME(s: AuthState, name: string | null) {
    if (s.user && typeof name === "string") {
      s.user = { ...s.user, displayName: name } as User;
    }
  },
  SET_THUMBNAIL(s: AuthState, url: string | null) {
    if (s.user && typeof url === "string") {
      s.user = { ...s.user, photoURL: url } as User;
    }
  },
};

const actions = {
  // Optional: If you want to keep auth state synced automatically.
  // Call store.dispatch('auth/initAuthListener') once on app start.
  initAuthListenerOnce({ state, commit, dispatch }: any) {
    if (state._readyPromise) return state._readyPromise;
    const ready = new Promise<User | null>((resolve) => {
      const unsub = auth.onAuthStateChanged(async (user) => {
        // Check 3-hour expiry
        if (user && isSessionExpired(user)) {
          try {
            await signOut(auth);
          } catch (e) {
            console.error("Error during signOut after expiry:", e);
          }
          user = null; // treat as logged out
        }

        commit("SET_USER", user ?? null);
        commit("SET_LOGGED_IN", !!user);

        // Hydrate Firestore user profile module (if you have a user module)
        try {
          if (user) {
            // this is your existing fetchUser(firebaseUser) action
            await dispatch("users/fetchUser", user, { root: true });

            // Restore the user's saved language preference on login
            await syncLocaleFromUser(
              user.uid,
              i18n.global.locale.value as string
            );
          } else {
            // optional: clear profile store if you have such an action
            if ((dispatch as any).length > 0) {
              // e.g. await dispatch("user/clearUser", null, { root: true })
            }
          }
        } catch (e) {
          console.error("Error in users/fetchUser:", e);
        }

        if (!state.initialized) {
          commit("SET_INITIALIZED", true);
          resolve(user ?? null);
        }
      });

      commit("SET_UNSUB", unsub);
    });

    commit("SET_READY_PROMISE", ready);
    return ready;
  },

  stopAuthListener({ commit }: any) {
    commit("SET_UNSUB", null);
    commit("SET_READY_PROMISE", null);
    commit("SET_INITIALIZED", false);
  },

  async register(
    { commit, dispatch }: any,
    payload: {
      email: string;
      password: string;
      reference: string;
      name?: string;
      phone?: string;
    },
  ) {
    const { user } = await createUserWithEmailAndPassword(
      auth,
      payload.email,
      payload.password,
    );

    if (payload.name) {
      await updateProfile(user, { displayName: payload.name });
    }

    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email: user.email ?? "",
      name: payload.name ?? "",
      phone: payload.phone ?? "",
      account: Number(payload.reference || 0),
      // The organisations they belong to, by reference, at their level in
      // each; see the organisations module's `switchTo`.
      organisations: Number(payload.reference || 0) > 0 ? { [String(Number(payload.reference))]: 0 } : {},
      roles: [],
      teams: [],
      isNew: true,
      thumbnail: "",
      usertype: 0,
      locale: getStoredLocale(),
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });

    commit("SET_USER", user);
    commit("SET_LOGGED_IN", true);
    await dispatch("users/fetchUser", user, { root: true });
  },

  async resetPassword(_ctx: any, { email }: { email: string }) {
    await sendPasswordResetEmail(auth, email);
    sendPasswordResetEmail(auth, email)
      .then(() => {
        return { success: true };
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.error(
          "Error sending password reset email:",
          errorCode,
          errorMessage,
        );
      });
  },

  async logIn(
    { commit, dispatch }: any,
    { email, password }: { email: string; password: string },
  ) {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    commit("SET_USER", user);
    commit("SET_LOGGED_IN", true);
    await dispatch("users/fetchUser", user, { root: true });
  },

  async logOut({ commit, dispatch }: any) {
    await signOut(auth);
    commit("SET_USER", null);
    commit("SET_LOGGED_IN", false);
    dispatch("users/reset", null, { root: true });
    return { success: true };
  },

  async setDisplayName(
    { commit }: any,
    payload: { name: string; callback?: () => void },
  ) {
    if (!auth.currentUser) return;
    await updateProfile(auth.currentUser, { displayName: payload.name });
    commit("SET_DISPLAY_NAME", payload.name);
    payload?.callback?.();
  },

  async updatePersonalInfo(
    { commit }: any,
    payload: {
      id: string;
      name: string | null;
      phone: string | null;
      callback?: () => void;
    },
  ) {
    const userDocRef = doc(db, "users", payload.id);
    await updateDoc(userDocRef, {
      name: payload.name,
      phone: payload.phone,
      updatedAt: serverTimestamp(),
    });

    if (payload.name !== null && auth.currentUser) {
      try {
        await updateProfile(auth.currentUser, { displayName: payload.name });
        commit("SET_DISPLAY_NAME", payload.name);
      } catch (err) {
        console.error(err);
      }
    }
    payload?.callback?.();
  },

  async updateThumbnail(
    { commit }: any,
    payload: { id: string; thumbnailURL: string; callback?: () => void },
  ) {
    const userDocRef = doc(db, "users", payload.id);
    await updateDoc(userDocRef, {
      thumbnail: payload.thumbnailURL,
      updatedAt: serverTimestamp(),
    });
    commit("SET_THUMBNAIL", payload.thumbnailURL);
    payload?.callback?.();
  },

  async deleteUser(_ctx: any, payload: { id: string; callback?: () => void }) {
    await deleteDoc(doc(db, "users", payload.id));
    payload?.callback?.();
  },

  // Stub kept from your original code; fill in storage logic when ready.
  async uploadAvatar(
    _ctx: any,
    payload: { file: File | null; userId: string },
  ) {
    if (!payload.file) return;
    // TODO: Add Firebase Storage upload logic here
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
